import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowLeft, BellRing, CheckCircle2, CreditCard, FileMinus2, Lock, Plus, Printer, RotateCcw, Send, Trash2, Unlock } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { formatCurrency, formatDate } from "@/lib/format";
import { issueInvoice, saveInvoiceDraft } from "@/lib/invoices";
import { usePricingRole } from "@/hooks/use-pricing-role";

export const Route = createFileRoute("/_authenticated/invoices/$id")({
  head: () => ({ meta: [
    { title: "Fakturadetaljer | VERQO" },
    { name: "description", content: "Rediger og følg opp faktura." },
    { property: "og:title", content: "Fakturadetaljer | VERQO" },
    { property: "og:description", content: "Rediger og følg opp faktura." },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary" },
  ] }),
  component: InvoiceEditor,
});

type Line = {
  id?: string;
  line_type: string;
  material_id?: string | null;
  labor_rate_id?: string | null;
  source_quote_item_id?: string | null;
  source_project_line_id?: string | null;
  description: string;
  quantity: number;
  unit: string;
  unit_cost: number;
  unit_price: number;
  vat_pct: number;
  sort_order: number;
};

type Invoice = {
  id: string; company_id: string; module_key: "electrician" | "carpenter";
  document_type: "invoice" | "credit_note"; invoice_number: number | null;
  credit_of_invoice_id: string | null; credit_reason: string | null;
  title: string; description: string | null;
  bill_to_name: string | null; bill_to_org_number: string | null; bill_to_address: string | null;
  bill_to_postal_code: string | null; bill_to_city: string | null; bill_to_email: string | null;
  site_address: string | null; site_postal_code: string | null; site_city: string | null;
  site_contact_name: string | null; site_contact_phone: string | null;
  reference_name: string | null; presentation: string; delivery_date: string | null;
  payment_terms_days: number; vat_percent: number;
  terms_payment: string | null; terms_included: string | null; terms_excluded: string | null; terms_reservations: string | null;
  is_final_invoice: boolean; issue_date: string | null; issued_at: string | null; due_date: string | null;
  total_incl_vat: number; subtotal_excl_vat: number; vat_amount: number;
  project_id: string | null; quote_id: string | null; customer_id: string | null;
  billing_method: string; billing_percent: number | null; source_total_excl_vat: number | null;
};

const STATUS: Record<string, string> = {
  draft: "Kladd", issued: "Utstedt", sent: "Sendt", partially_paid: "Delvis betalt",
  paid: "Betalt", credited: "Kreditert", partially_credited: "Delvis kreditert", overdue: "Forfalt",
};

const newRequestId = () => (globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`);

function InvoiceEditor() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { canManagePricing, isLoading: roleLoading } = usePricingRole();

  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [lines, setLines] = useState<Line[]>([]);
  const [dirty, setDirty] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [creditOpen, setCreditOpen] = useState(false);
  const [creditQty, setCreditQty] = useState<Record<string, number>>({});
  const [creditReason, setCreditReason] = useState("");
  const [amount, setAmount] = useState(0);
  const [paidOn, setPaidOn] = useState(new Date().toISOString().slice(0, 10));

  const query = useQuery({
    queryKey: ["invoice", id],
    enabled: canManagePricing,
    queryFn: async () => {
      const [{ data: inv, error }, { data: items, error: lineError }, { data: payments, error: payError }, { data: statusRow }, { data: preflight }] = await Promise.all([
        supabase.from("invoices").select("*").eq("id", id).single(),
        supabase.from("invoice_lines").select("*").eq("invoice_id", id).order("sort_order"),
        supabase.from("invoice_payments").select("id,amount,payment_date,method,note").eq("invoice_id", id).order("payment_date"),
        supabase.from("invoice_status_v").select("*").eq("invoice_id", id).maybeSingle(),
        supabase.rpc("invoice_preflight", { _invoice_id: id }),
      ]);
      if (error) throw error;
      if (lineError) throw lineError;
      if (payError) throw payError;
      return {
        invoice: inv as unknown as Invoice,
        lines: (items ?? []) as unknown as Line[],
        payments: payments ?? [],
        statusRow,
        preflight: (preflight ?? []) as Array<{ code: string; severity: string; message: string; fix_label: string | null; fix_route: string | null }>,
      };
    },
  });

  useEffect(() => {
    if (query.data) { setInvoice(query.data.invoice); setLines(query.data.lines); setDirty(false); }
  }, [query.data]);

  const totals = useMemo(() => lines.reduce((acc, line) => {
    const net = Number(line.quantity) * Number(line.unit_price);
    acc.net += net; acc.vat += net * Number(line.vat_pct) / 100; return acc;
  }, { net: 0, vat: 0 }), [lines]);

  const statusRow = query.data?.statusRow;
  const status = String(statusRow?.status ?? (invoice?.issued_at ? "issued" : "draft"));
  const paidNet = Number(statusRow?.paid_net ?? 0);
  const remaining = Math.max(0, Number(statusRow?.net_outstanding ?? Number(invoice?.total_incl_vat ?? 0) - paidNet));
  const editable = Boolean(invoice) && invoice?.issued_at === null && invoice?.document_type === "invoice";
  const overdue = Boolean(statusRow?.is_overdue);
  const preflightRows = query.data?.preflight;
  // Databasen svarer med rød/gul/grønn. Tomt svar betyr ukjent tilstand – da skal utstedelse blokkeres.
  const blockers = (preflightRows ?? []).filter((row) => row.severity === "rød");
  const warnings = (preflightRows ?? []).filter((row) => row.severity === "gul");
  const preflightUnknown = !preflightRows || preflightRows.length === 0;

  const update = <K extends keyof Invoice>(key: K, value: Invoice[K]) => {
    setInvoice((old) => (old ? { ...old, [key]: value } : old)); setDirty(true);
  };
  const updateLine = (index: number, patch: Partial<Line>) => {
    setLines((old) => old.map((line, i) => (i === index ? { ...line, ...patch } : line))); setDirty(true);
  };

  const save = useMutation({
    mutationFn: async () => {
      if (!invoice) return;
      const header = {
        id: invoice.id,
        company_id: invoice.company_id,
        module_key: invoice.module_key,
        quote_id: invoice.quote_id,
        project_id: invoice.project_id,
        customer_id: invoice.customer_id,
        bill_to_name: invoice.bill_to_name,
        bill_to_org_number: invoice.bill_to_org_number,
        bill_to_address: invoice.bill_to_address,
        bill_to_postal_code: invoice.bill_to_postal_code,
        bill_to_city: invoice.bill_to_city,
        bill_to_email: invoice.bill_to_email,
        site_address: invoice.site_address,
        site_postal_code: invoice.site_postal_code,
        site_city: invoice.site_city,
        site_contact_name: invoice.site_contact_name,
        site_contact_phone: invoice.site_contact_phone,
        title: invoice.title,
        description: invoice.description,
        reference_name: invoice.reference_name,
        terms_payment: invoice.terms_payment,
        terms_included: invoice.terms_included,
        terms_excluded: invoice.terms_excluded,
        terms_reservations: invoice.terms_reservations,
        presentation: invoice.presentation,
        billing_method: invoice.billing_method,
        billing_percent: invoice.billing_percent,
        is_final_invoice: invoice.is_final_invoice,
        vat_percent: invoice.vat_percent,
        delivery_date: invoice.delivery_date,
        payment_terms_days: Number(invoice.payment_terms_days),
        source_total_excl_vat: invoice.source_total_excl_vat,
      };
      const payload = lines.filter((line) => line.description.trim()).map((line, index) => ({
        line_type: line.line_type,
        material_id: line.material_id ?? null,
        labor_rate_id: line.labor_rate_id ?? null,
        source_quote_item_id: line.source_quote_item_id ?? null,
        source_project_line_id: line.source_project_line_id ?? null,
        allocation_method: "amount",
        description: line.description,
        quantity: Number(line.quantity),
        unit: line.unit,
        unit_cost: Number(line.unit_cost),
        unit_price: Number(line.unit_price),
        vat_pct: Number(line.vat_pct),
        sort_order: index,
      }));
      await saveInvoiceDraft(header, payload);
    },
    onSuccess: () => { setDirty(false); qc.invalidateQueries({ queryKey: ["invoice", id] }); toast.success("Kladd lagret"); },
    onError: (error: Error) => toast.error(error.message),
  });

  const issue = useMutation({
    mutationFn: async () => {
      if (dirty) await save.mutateAsync();
      if (!invoice) throw new Error("Faktura mangler");
      return issueInvoice(id, invoice.company_id, new Date().toISOString().slice(0, 10));
    },
    onSuccess: (number) => {
      qc.invalidateQueries({ queryKey: ["invoice", id] });
      qc.invalidateQueries({ queryKey: ["invoices"] });
      toast.success(`Faktura ${number} er utstedt og låst`);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const markSent = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc("mark_invoice_sent", { _invoice_id: id, _channel: "manual", _recipient: invoice?.bill_to_email ?? undefined });
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["invoice", id] }); toast.success("Markert som sendt"); },
    onError: (error: Error) => toast.error(error.message),
  });

  const pay = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc("register_invoice_payment", {
        _invoice_id: id, _amount: amount, _client_request_id: newRequestId(), _payment_date: paidOn, _method: "Manuell",
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setPaymentOpen(false);
      qc.invalidateQueries({ queryKey: ["invoice", id] });
      qc.invalidateQueries({ queryKey: ["invoices"] });
      toast.success("Betaling registrert");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const reversePayment = useMutation({
    mutationFn: async (payment: { id: string; amount: number }) => {
      const { error } = await supabase.rpc("reverse_invoice_payment", {
        _payment_id: payment.id, _amount: payment.amount, _client_request_id: newRequestId(), _reason: "Reversert manuelt",
      });
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["invoice", id] }); toast.success("Betaling reversert"); },
    onError: (error: Error) => toast.error(error.message),
  });

  const credit = useMutation({
    mutationFn: async () => {
      const selected = lines
        .filter((line) => line.id && (creditQty[line.id] ?? 0) > 0)
        .map((line) => ({ original_line_id: line.id, quantity: creditQty[line.id!] }));
      if (selected.length === 0) throw new Error("Velg minst én linje å kreditere");
      const { data, error } = await supabase.rpc("create_credit_note", {
        _original_invoice_id: id,
        _lines: selected as never,
        _reason: creditReason.trim() || `Kreditering av faktura ${invoice?.invoice_number ?? ""}`,
      });
      if (error) throw error;
      return String(data);
    },
    onSuccess: (newId) => { setCreditOpen(false); navigate({ to: "/invoices/$id", params: { id: newId } }); },
    onError: (error: Error) => toast.error(error.message),
  });

  const reopen = useMutation({
    mutationFn: async () => {
      if (!invoice?.project_id) throw new Error("Fakturaen er ikke knyttet til et prosjekt");
      const { error } = await supabase.rpc("reopen_project_billing", { _project_id: invoice.project_id });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invoice", id] });
      qc.invalidateQueries({ queryKey: ["invoices"] });
      toast.success("Prosjektet er låst opp for videre fakturering");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const del = useMutation({
    mutationFn: async () => { const { error } = await supabase.from("invoices").delete().eq("id", id); if (error) throw error; },
    onSuccess: () => navigate({ to: "/invoices" }),
    onError: (error: Error) => toast.error(error.message),
  });

  if (roleLoading) return <div className="py-20 text-center text-muted-foreground">Laster…</div>;
  if (!canManagePricing) return <div className="card p-8 text-center"><h1 className="text-xl font-semibold">Ingen tilgang</h1><p className="mt-2 text-sm text-muted-foreground">Faktura er kun tilgjengelig for eier og administrator.</p></div>;
  if (!invoice) return <div className="py-20 text-center text-muted-foreground">Laster faktura…</div>;

  const isCreditNote = invoice.document_type === "credit_note";

  return <>
    <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
      <Button asChild variant="ghost"><Link to="/invoices"><ArrowLeft className="h-4 w-4"/>Faktura</Link></Button>
      <div className="flex flex-wrap gap-2">
        {editable ? <>
          <Button variant="outline" onClick={() => del.mutate()}><Trash2 className="h-4 w-4"/>Slett kladd</Button>
          <Button variant="outline" onClick={() => save.mutate()} disabled={!dirty || save.isPending}>Lagre</Button>
          <Button onClick={() => { if (confirm("Utstede og låse fakturaen? Dette kan ikke angres.")) issue.mutate(); }} disabled={blockers.length > 0 || preflightUnknown || issue.isPending}><Lock className="h-4 w-4"/>Utsted</Button>
        </> : <>
          <Button asChild variant="outline"><Link to="/invoices/$id/pdf" params={{ id }} target="_blank"><Printer className="h-4 w-4"/>PDF</Link></Button>
          {!isCreditNote && !["paid", "credited"].includes(status) && <Button variant="outline" onClick={() => { setAmount(remaining); setPaymentOpen(true); }}><CreditCard className="h-4 w-4"/>Registrer betaling</Button>}
          {status === "issued" && <Button onClick={() => markSent.mutate()}><Send className="h-4 w-4"/>Marker sendt</Button>}
          {!isCreditNote && overdue && <Button asChild variant="outline"><Link to="/invoices/$id/purring" params={{ id }} target="_blank"><BellRing className="h-4 w-4"/>Purring</Link></Button>}
          {!isCreditNote && invoice.is_final_invoice && invoice.project_id && <Button variant="outline" onClick={() => { if (confirm("Låse opp prosjektet for videre fakturering?")) reopen.mutate(); }}><Unlock className="h-4 w-4"/>Lås opp prosjekt</Button>}
          {!isCreditNote && status !== "credited" && <Button variant="outline" onClick={() => { setCreditQty(Object.fromEntries(lines.flatMap((line) => (line.id ? [[line.id, Math.abs(Number(line.quantity))]] : [])))); setCreditReason(""); setCreditOpen(true); }}><FileMinus2 className="h-4 w-4"/>Krediter</Button>}
        </>}
      </div>
    </div>

    <div className="mb-5 flex flex-wrap items-center gap-3">
      <div>
        <h1 className="text-2xl font-semibold">{isCreditNote ? "Kreditnota" : "Faktura"} {invoice.invoice_number ?? "– kladd"}</h1>
        <p className="text-sm text-muted-foreground">{invoice.title}</p>
      </div>
      <span className="chip">{STATUS[status] ?? status}</span>
      {!editable && <span className="flex items-center gap-1 text-xs text-muted-foreground"><Lock className="h-3 w-3"/>Dokumentet er låst</span>}
    </div>

    {editable && preflightUnknown && (
      <div className="mb-5 rounded-md border border-warning/40 bg-warning/10 p-4 text-sm">
        <div className="flex items-center gap-2 font-medium"><AlertTriangle className="h-4 w-4"/>Kontrollen er ikke fullført</div>
        <p className="mt-1">Vi fikk ikke svar på kontrollen før utstedelse. Last siden på nytt før du utsteder.</p>
      </div>
    )}

    {editable && (blockers.length > 0 || warnings.length > 0) && (
      <div className={`mb-5 rounded-md border p-4 text-sm ${blockers.length > 0 ? "border-destructive/40 bg-destructive/10" : "border-warning/40 bg-warning/10"}`}>
        <div className="mb-2 flex items-center gap-2 font-medium"><AlertTriangle className="h-4 w-4"/>{blockers.length > 0 ? "Må rettes før utstedelse" : "Til kontroll"}</div>
        <ul className="space-y-1">
          {[...blockers, ...warnings].map((row) => <li key={row.code}>{row.message}</li>)}
        </ul>
      </div>
    )}

    <div className="grid gap-5 lg:grid-cols-[1fr,320px]">
      <div className="space-y-5">
        <section className="card p-5">
          <h2 className="mb-4 font-semibold">Fakturamottaker og oppdrag</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Kunde" value={invoice.bill_to_name ?? ""} disabled={!editable} onChange={(v) => update("bill_to_name", v)}/>
            <Field label="Org.nr" value={invoice.bill_to_org_number ?? ""} disabled={!editable} onChange={(v) => update("bill_to_org_number", v)}/>
            <Field label="Fakturaadresse" value={invoice.bill_to_address ?? ""} disabled={!editable} onChange={(v) => update("bill_to_address", v)}/>
            <Field label="Postnr / sted" value={[invoice.bill_to_postal_code, invoice.bill_to_city].filter(Boolean).join(" ")} disabled={!editable} onChange={(v) => { const [postal, ...city] = v.split(" "); update("bill_to_postal_code", postal); update("bill_to_city", city.join(" ")); }}/>
            <Field label="E-post" value={invoice.bill_to_email ?? ""} disabled={!editable} onChange={(v) => update("bill_to_email", v)}/>
            <Field label="Leveringsdato" type="date" value={invoice.delivery_date ?? ""} disabled={!editable} onChange={(v) => update("delivery_date", v || null)}/>
            <Field label="Arbeidssted" value={invoice.site_address ?? ""} disabled={!editable} onChange={(v) => update("site_address", v)}/>
            <Field label="Vår ref" value={invoice.reference_name ?? ""} disabled={!editable} onChange={(v) => update("reference_name", v)}/>
          </div>
          <label className="label-base mt-3">Beskrivelse av oppdraget</label>
          <textarea className="input-base" rows={3} value={invoice.description ?? ""} disabled={!editable} onChange={(e) => update("description", e.target.value)}/>
        </section>

        <section className="card p-5">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-semibold">Fakturalinjer</h2>
            {editable && <Button size="sm" variant="outline" onClick={() => { setLines([...lines, { line_type: "service", description: "", quantity: 1, unit: "stk", unit_cost: 0, unit_price: 0, vat_pct: invoice.vat_percent, sort_order: lines.length }]); setDirty(true); }}><Plus className="h-4 w-4"/>Linje</Button>}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px] text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="pb-2">Beskrivelse</th><th>Antall</th><th>Enhet</th><th>Pris eks. mva</th><th className="text-right">Sum</th><th/></tr></thead>
              <tbody>
                {lines.map((line, index) => (
                  <tr key={line.id ?? index} className="border-b">
                    <td className="py-2 pr-2"><input className="input-base" disabled={!editable} value={line.description} onChange={(e) => updateLine(index, { description: e.target.value })}/></td>
                    <td className="w-24 pr-2"><input className="input-base text-right" type="number" disabled={!editable} value={line.quantity} onChange={(e) => updateLine(index, { quantity: Number(e.target.value) })}/></td>
                    <td className="w-24 pr-2"><input className="input-base" disabled={!editable} value={line.unit} onChange={(e) => updateLine(index, { unit: e.target.value })}/></td>
                    <td className="w-36 pr-2"><input className="input-base text-right" type="number" disabled={!editable} value={line.unit_price} onChange={(e) => updateLine(index, { unit_price: Number(e.target.value) })}/></td>
                    <td className="text-right font-medium">{formatCurrency(Number(line.quantity) * Number(line.unit_price))}</td>
                    <td>{editable && <Button size="icon-sm" variant="ghost" onClick={() => { setLines(lines.filter((_, i) => i !== index)); setDirty(true); }}><Trash2 className="h-4 w-4"/></Button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <aside className="space-y-4">
        <section className="card p-5">
          <h2 className="mb-3 font-semibold">Oppsummering</h2>
          <Row label="Sum eks. mva" value={formatCurrency(editable ? totals.net : invoice.subtotal_excl_vat)}/>
          <Row label={`MVA (${invoice.vat_percent} %)`} value={formatCurrency(editable ? totals.vat : invoice.vat_amount)}/>
          <div className="my-3 border-t"/>
          <Row label="Å betale" value={formatCurrency(editable ? totals.net + totals.vat : invoice.total_incl_vat)} strong/>
          {!editable && <>
            <Row label="Registrert betalt" value={formatCurrency(paidNet)}/>
            {Number(statusRow?.credited_amount ?? 0) > 0 && <Row label="Kreditert" value={formatCurrency(Number(statusRow?.credited_amount))}/>}
            <Row label="Gjenstår" value={formatCurrency(remaining)} strong/>
          </>}
        </section>

        {editable && <section className="card p-5 space-y-3">
          <h2 className="font-semibold">Fakturavalg</h2>
          <div>
            <label className="label-base">Presentasjon</label>
            <select className="input-base" value={invoice.presentation} onChange={(e) => update("presentation", e.target.value)}>
              <option value="itemized">Spesifisert</option>
              <option value="fixed_price">Fastpris</option>
            </select>
          </div>
          <Field label="Betalingsfrist (dager)" type="number" value={String(invoice.payment_terms_days)} onChange={(v) => update("payment_terms_days", Number(v))}/>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={invoice.is_final_invoice} onChange={(e) => update("is_final_invoice", e.target.checked)}/>Sluttfaktura – lås prosjektet</label>
          {invoice.source_total_excl_vat != null && <div className="rounded-md bg-muted p-3 text-xs">
            <Row label="Kontraktssum eks. mva" value={formatCurrency(invoice.source_total_excl_vat)}/>
            <Row label="Denne kladden" value={formatCurrency(totals.net)}/>
            <Row label="Gjenstår" value={formatCurrency(Number(invoice.source_total_excl_vat) - totals.net)}/>
          </div>}
        </section>}

        {(query.data?.payments ?? []).length > 0 && <section className="card p-5">
          <h2 className="mb-3 font-semibold">Betalinger</h2>
          {query.data?.payments.map((payment) => (
            <div key={payment.id} className="flex items-center justify-between gap-2 border-b py-2 text-sm last:border-0">
              <span>{formatDate(payment.payment_date)}</span>
              <span className="font-medium">{formatCurrency(payment.amount)}</span>
              <Button size="icon-sm" variant="ghost" title="Reverser betaling" onClick={() => { if (confirm("Reversere hele denne betalingen?")) reversePayment.mutate({ id: payment.id, amount: Number(payment.amount) }); }}><RotateCcw className="h-4 w-4"/></Button>
            </div>
          ))}
        </section>}
      </aside>
    </div>

    <Dialog open={creditOpen} onOpenChange={setCreditOpen}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Kreditnota</DialogTitle>
          <DialogDescription>Full eller delvis kreditering. Sett antall til 0 for linjer som ikke skal krediteres.</DialogDescription>
        </DialogHeader>
        <div className="max-h-80 overflow-y-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="pb-2">Linje</th><th className="pb-2 text-right">Fakturert</th><th className="pb-2 text-right">Krediteres</th><th className="pb-2 text-right">Beløp</th></tr></thead>
            <tbody>
              {lines.map((line, index) => {
                const key = line.id ?? String(index);
                const qty = creditQty[key] ?? 0;
                const max = Math.abs(Number(line.quantity));
                return <tr key={key} className="border-b">
                  <td className="py-2 pr-2">{line.description}</td>
                  <td className="text-right text-muted-foreground">{max} {line.unit}</td>
                  <td className="w-28 py-1 pl-2"><input className="input-base text-right" type="number" min={0} max={max} value={qty} onChange={(e) => setCreditQty({ ...creditQty, [key]: Math.min(max, Math.max(0, Number(e.target.value))) })}/></td>
                  <td className="text-right">{formatCurrency(-qty * Number(line.unit_price))}</td>
                </tr>;
              })}
            </tbody>
          </table>
        </div>
        <div className="mt-3 flex justify-between text-sm font-semibold">
          <span>Sum kreditering eks. mva</span>
          <span>{formatCurrency(-lines.reduce((sum, line, index) => sum + (creditQty[line.id ?? String(index)] ?? 0) * Number(line.unit_price), 0))}</span>
        </div>
        <div className="mt-3">
          <label className="label-base">Årsak</label>
          <input className="input-base" value={creditReason} onChange={(e) => setCreditReason(e.target.value)} placeholder="Årsak til kreditering"/>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setCreditOpen(false)}>Avbryt</Button>
          <Button variant="outline" onClick={() => setCreditQty(Object.fromEntries(lines.flatMap((line) => (line.id ? [[line.id, Math.abs(Number(line.quantity))]] : []))))}>Full kreditering</Button>
          <Button onClick={() => credit.mutate()} disabled={credit.isPending}>Opprett kreditnota</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={paymentOpen} onOpenChange={(open) => { setPaymentOpen(open); if (open) setAmount(remaining); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Registrer betaling</DialogTitle>
          <DialogDescription>Delbetaling støttes. Status oppdateres automatisk.</DialogDescription>
        </DialogHeader>
        <Field label="Beløp" type="number" value={String(amount)} onChange={(v) => setAmount(Number(v))}/>
        <Field label="Betalingsdato" type="date" value={paidOn} onChange={setPaidOn}/>
        <DialogFooter>
          <Button variant="outline" onClick={() => setPaymentOpen(false)}>Avbryt</Button>
          <Button onClick={() => pay.mutate()} disabled={pay.isPending || amount <= 0 || amount > remaining}><CheckCircle2 className="h-4 w-4"/>Registrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>;
}

function Field({ label, value, onChange, type = "text", disabled = false }: { label: string; value: string; onChange: (v: string) => void; type?: string; disabled?: boolean }) {
  return <div><label className="label-base">{label}</label><input className="input-base" type={type} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}/></div>;
}

function Row({ label, value, strong = false }: { label: string; value: string; strong?: boolean }) {
  return <div className={`flex justify-between gap-3 py-1 text-sm ${strong ? "font-semibold" : ""}`}><span>{label}</span><span>{value}</span></div>;
}
